// Errori del browser mandati al backend (D61): un problema sul telefono di un agente non deve restare invisibile.
// Prende gli errori non gestiti (window.onerror, promesse rifiutate senza catch, errori di Vue e del router) e li
// manda a /errori-client, dove finiscono nel log e nel contatore controllato da Zabbix. Con i file .map (D60) la
// riga del codice compresso si riporta ai sorgenti. Mai più di 10 per pagina caricata, e ognuno una volta sola.
// Passa da api/client.ts come ogni chiamata al backend (regola 5).
import type { App } from 'vue';
import { api, ErroreApi } from './api/client';
import { useSessione } from './stores/sessione';
import { versioneApp } from './versione';

type Tipo = 'errore' | 'promessa' | 'vue' | 'router';
const MASSIMO = 10;
const giaMandati = new Set<string>();

const taglia = (t: unknown, n: number) => (t == null ? null : String(t).slice(0, n));

/** Errori che non dicono niente di utile o che il router gestisce già (ricarica dopo una pubblicazione). */
function daIgnorare(messaggio: string): boolean {
  return /ResizeObserver loop|Script error\.?$|dynamically imported module|Importing a module script failed|error loading dynamically imported/i.test(messaggio);
}

export function segnalaErrore(tipo: Tipo, errore: unknown, extra: { sorgente?: string; riga?: number; colonna?: number } = {}): void {
  try {
    // Rete assente e sessione scaduta non sono errori del programma
    if (errore instanceof ErroreApi && (errore.stato === 0 || errore.stato === 401)) return;
    const e = errore instanceof Error ? errore : null;
    const messaggio = (e ? `${e.name}: ${e.message}` : String(errore ?? 'errore sconosciuto')).slice(0, 500);
    if (daIgnorare(messaggio)) return;
    const chiave = `${tipo}|${messaggio}|${extra.sorgente ?? ''}|${extra.riga ?? ''}`;
    if (giaMandati.has(chiave) || giaMandati.size >= MASSIMO) return;
    giaMandati.add(chiave);
    let utente: string | null = null;
    try {
      utente = useSessione().utente?.login ?? null;
    } catch {
      // pinia non ancora pronta
    }
    void api.POST('/errori-client', {
      body: {
        tipo, messaggio,
        sorgente: taglia(extra.sorgente, 300), riga: extra.riga ?? null, colonna: extra.colonna ?? null,
        stack: taglia(e?.stack, 4000),
        pagina: (window.location.hash.replace(/^#/, '') || '/').slice(0, 300),
        versione: versioneApp.slice(0, 40),
        utente: utente?.slice(0, 30) ?? null,
      },
    }).catch(() => undefined);
  } catch {
    // la segnalazione di un errore non deve mai causarne un altro
  }
}

/** Da chiamare una volta, all'avvio, con l'app Vue. */
export function installaSegnalazioneErrori(app: App): void {
  window.addEventListener('error', (ev) => {
    // Gli errori di caricamento di immagini e script non hanno ev.error né un messaggio utile
    if (!ev.message) return;
    segnalaErrore('errore', ev.error ?? ev.message, { sorgente: ev.filename, riga: ev.lineno, colonna: ev.colno });
  });
  window.addEventListener('unhandledrejection', (ev) => segnalaErrore('promessa', ev.reason));
  app.config.errorHandler = (err, _istanza, info) => {
    console.error(err);
    segnalaErrore('vue', err instanceof Error ? err : new Error(`${String(err)} (${info})`));
  };
}
